/**
 * Enum for discount types.
 */
export const DISCOUNT_TYPES = Object.freeze({
  PERCENTAGE: "PERCENTAGE",
  FIXED_PRICE: "FIXED_PRICE"
});

/**
 * Returns whether a discount has passed its end time. A discount with no end time never ends.
 *
 * @param {Object} discount
 * @param {string|null} [discount.validityEndTime]
 * @returns {boolean}
 */
export const hasDiscountEnded = ({ validityEndTime } = {}) =>
  Boolean(validityEndTime) && new Date(validityEndTime) <= new Date();

/**
 * Maximum length of one sale banner text. Matches the backend limit.
 */
export const BANNER_TEXT_MAX_LENGTH = 150;

/**
 * Placeholder in a slot banner text that the website replaces with the number of slots left.
 */
export const REMAINING_SLOTS_PLACEHOLDER = "{{remainingSlots}}";

/**
 * Sale banner text fields of a discount, one per sale state. `key` is the backend `bannerTexts` key,
 * and `placeholder` is the website default text for that state.
 */
export const BANNER_TEXT_FIELDS = Object.freeze([
  {
    key: "upcoming",
    label: "Before the sale starts",
    help: "Shows until the start time, with a countdown to the start.",
    placeholder: "🎁 Special Sale Coming Soon!"
  },
  {
    key: "liveWithSlotsAndEndTime",
    label: "Live, with a usage limit and an end time",
    help: `Shows with a countdown to the end time. Use ${REMAINING_SLOTS_PLACEHOLDER} for the slots left.`,
    placeholder: `🔥 Sale Alert: Only ${REMAINING_SLOTS_PLACEHOLDER} slots remain! ⏳`
  },
  {
    key: "liveWithSlots",
    label: "Live, with a usage limit and no end time",
    help: `Shows with no countdown. Use ${REMAINING_SLOTS_PLACEHOLDER} for the slots left.`,
    placeholder: `🔥 Sale Alert: Only ${REMAINING_SLOTS_PLACEHOLDER} slots remain! ⏳`
  },
  {
    key: "liveWithEndTime",
    label: "Live, with an end time and no usage limit",
    help: "Shows with a countdown to the end time.",
    placeholder: "Early Bird Sale For Prime ⏳"
  },
  {
    key: "liveOpenEnded",
    label: "Live, with no usage limit and no end time",
    help: "Shows with no countdown.",
    placeholder: "Early Bird Sale For Prime ⏳"
  }
]);

/**
 * Checks if a private discount has a targeting rule the backend can match a user against.
 *
 * @param {object} discount
 * @param {Array<string>} [discount.targetEmailDomains]
 * @param {Array<string>} [discount.targetBatchIds]
 * @param {Array<string>} [discount.targetCourseIds]
 * @returns {boolean} True when at least one domain, batch or course rule is set.
 */
export const hasTargetingRules = ({ targetEmailDomains, targetBatchIds, targetCourseIds } = {}) =>
  [targetEmailDomains, targetBatchIds, targetCourseIds].some((rules) => rules?.length > 0);

/**
 * Resolves who a discount reaches, as the backend eligibility filter decides it.
 *
 * The backend treats `alumniOnly` as an extra condition on top of visibility:
 * - A public discount reaches every user.
 * - A private discount reaches only users who match a targeting rule, the email
 *   allowlist or a waitlist allowlist.
 * - `alumniOnly` then removes every user who is not alumni.
 *
 * So a private discount with no rules and no allowlist reaches nobody, alumni included.
 *
 * A FIXED_PRICE discount is the exception. The upgrade flow loads it by the coupon code in app config,
 * and skips the rules above, so it reaches upgrade users.
 *
 * @param {object} discount - A discount row from the admin list API, or form values.
 * @param {string} [discount.discountType]
 * @param {boolean} discount.isPublic
 * @param {boolean} [discount.alumniOnly]
 * @param {Array<string>} [discount.targetEmailDomains]
 * @param {Array<string>} [discount.targetBatchIds]
 * @param {Array<string>} [discount.targetCourseIds]
 * @param {number} [discount.allowlistEmailCount]
 * @param {number} [discount.allowlistWaitlistCount]
 * @returns {{ label: string, color: string, reachesNobody: boolean }} The audience tag.
 */
export const resolveDiscountAudience = (discount = {}) => {
  const {
    discountType,
    isPublic,
    alumniOnly = false,
    allowlistEmailCount = 0,
    allowlistWaitlistCount = 0
  } = discount;

  if (discountType === DISCOUNT_TYPES.FIXED_PRICE) {
    return { label: "Upgrade users", color: "purple", reachesNobody: false };
  }

  if (isPublic) {
    return alumniOnly
      ? { label: "All alumni", color: "gold", reachesNobody: false }
      : { label: "All users", color: "green", reachesNobody: false };
  }

  if (hasTargetingRules(discount)) {
    return alumniOnly
      ? { label: "Targeted alumni", color: "gold", reachesNobody: false }
      : { label: "Targeted users", color: "geekblue", reachesNobody: false };
  }

  const allowlistParts = [];
  if (allowlistEmailCount > 0) {
    allowlistParts.push(`${allowlistEmailCount} email${allowlistEmailCount === 1 ? "" : "s"}`);
  }
  if (allowlistWaitlistCount > 0) {
    allowlistParts.push(
      `${allowlistWaitlistCount} waitlist${allowlistWaitlistCount === 1 ? "" : "s"}`
    );
  }

  if (allowlistParts.length > 0) {
    const allowlistSummary = allowlistParts.join(", ");
    if (alumniOnly) {
      return {
        label: `Allowlisted alumni (${allowlistSummary})`,
        color: "gold",
        reachesNobody: false
      };
    }
    return { label: `Allowlist only (${allowlistSummary})`, color: "blue", reachesNobody: false };
  }

  return { label: "Reaches nobody", color: "red", reachesNobody: true };
};
